import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ShieldCheck, LogOut, ExternalLink, Users, Clock, CheckCircle2, Rocket, Lock, Loader2,
} from 'lucide-react';
import { adminLogin, adminStats, adminUsers as fetchAdminUsers, adminCompanies } from '../api';
import GrantShares from '../components/admin/GrantShares';
import { KycReview, AddMarket, Companies } from '../components/admin/AdminMisc';
import CryptoVault from '../components/admin/CryptoVault';
import PriceControl from '../components/admin/PriceControl';

export default function AdminPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => sessionStorage.getItem('af_admin_token') || '');
  const [creds, setCreds] = useState({ user: '', password: '' });
  const [tab, setTab] = useState('grant');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async (tk) => {
    const t = tk || token;
    if (!t) return;
    setLoading(true);
    try {
      const [s, u, c] = await Promise.all([adminStats(t), fetchAdminUsers(t), adminCompanies(t)]);
      setStats(s);
      setUsers(u);
      setCompanies(c);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem('af_admin_token');
        setToken('');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) refresh(token);
  }, [token, refresh]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await adminLogin(creds.user, creds.password);
      sessionStorage.setItem('af_admin_token', res.token);
      setToken(res.token);
      toast.success('Welcome back, admin');
    } catch {
      toast.error('Invalid credentials');
    }
  };

  const logout = () => {
    sessionStorage.removeItem('af_admin_token');
    setToken('');
    setCreds({ user: '', password: '' });
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <form onSubmit={login} className="relative w-full max-w-sm rounded-2xl bg-[var(--panel)] border border-[var(--border)] p-8">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[var(--orange)] to-[#c2410c] flex items-center justify-center">
              <ShieldCheck size={20} className="text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-[18px] text-white">APEXFORGE <span className="text-[var(--orange)]">ADMIN</span></div>
              <div className="text-[9px] tracking-[0.18em] text-[var(--muted-2)] font-mono">CONTROL CONSOLE</div>
            </div>
          </div>
          <div className="mt-7 space-y-4">
            <div>
              <label className="text-[10px] font-mono tracking-widest text-[var(--muted-2)]">USERNAME</label>
              <input value={creds.user} onChange={(e) => setCreds({ ...creds, user: e.target.value })} placeholder="admin" className="w-full mt-1.5 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-[14px] text-white placeholder:text-[var(--muted-2)] focus:outline-none focus:border-[var(--orange)] transition-colors" />
            </div>
            <div>
              <label className="text-[10px] font-mono tracking-widest text-[var(--muted-2)]">PASSWORD</label>
              <input type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} placeholder="••••••••" className="w-full mt-1.5 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-[14px] text-white placeholder:text-[var(--muted-2)] focus:outline-none focus:border-[var(--orange)] transition-colors" />
            </div>
            <button type="submit" className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-[var(--orange)] text-white font-semibold text-[14px] hover:bg-[var(--orange-bright)] transition-colors">
              <Lock size={15} /> SIGN IN
            </button>
            <Link to="/" className="block text-center text-[12px] text-[var(--muted)] hover:text-white transition-colors">← Back to markets</Link>
          </div>
        </form>
      </div>
    );
  }

  const statCards = [
    ['TOTAL USERS', stats?.totalUsers ?? '—', Users, '#60a5fa', 'rgba(96,165,250,0.12)'],
    ['KYC PENDING', stats?.kycPending ?? '—', Clock, 'var(--orange)', 'rgba(249,115,22,0.12)'],
    ['KYC VERIFIED', stats?.kycVerified ?? '—', CheckCircle2, 'var(--green)', 'rgba(22,199,132,0.12)'],
    ['PIPELINE MARKETS', stats?.pipelineMarkets ?? '—', Rocket, '#c084fc', 'rgba(192,132,252,0.12)'],
  ];

  const tabs = [
    ['kyc', 'KYC REVIEW'],
    ['market', 'ADD MARKET'],
    ['companies', 'COMPANIES'],
    ['grant', 'GRANT SHARES'],
    ['prices', 'LIVE PRICES'],
    ['vault', 'CRYPTO VAULT'],
  ];

  return (
    <div className="min-h-screen">
      <header className="border-b border-[var(--border)] bg-[rgba(10,10,12,0.9)]">
        <div className="max-w-[1320px] mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[var(--orange)] to-[#c2410c] flex items-center justify-center">
              <ShieldCheck size={18} className="text-white" />
            </div>
            <div className="leading-none">
              <div className="font-display font-bold text-[17px] text-white">APEXFORGE <span className="text-[var(--orange)]">ADMIN</span></div>
              <div className="text-[9px] tracking-[0.18em] text-[var(--muted-2)] mt-0.5 font-mono">CONTROL CONSOLE</div>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-[12px] px-3 py-2 rounded-md border border-[var(--border)] text-[var(--muted)] hover:text-white hover:border-[#3a3a42] transition-colors">
              <ExternalLink size={14} /> SITE
            </button>
            <button onClick={logout} className="flex items-center gap-1.5 text-[12px] px-3 py-2 rounded-md border border-[#5a2020] text-[var(--red)] hover:bg-[#2a1010] transition-colors">
              <LogOut size={14} /> LOGOUT
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1320px] mx-auto px-5 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map(([label, value, Ico, color, bg]) => (
            <div key={label} className="rounded-xl bg-[var(--panel)] border border-[var(--border)] p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: bg }}>
                <Ico size={20} style={{ color }} />
              </div>
              <div>
                <div className="text-[10px] font-mono tracking-[0.12em] text-[var(--muted-2)]">{label}</div>
                <div className="font-mono font-semibold text-[26px] text-white leading-tight mt-0.5">{value}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2.5 mt-7">
          {tabs.map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-4 py-2 rounded-lg text-[12px] font-semibold tracking-wide border transition-colors ${
                tab === id
                  ? 'bg-[var(--orange)] border-[var(--orange)] text-white'
                  : 'bg-[var(--panel)] border-[var(--border)] text-[var(--muted)] hover:text-white hover:border-[#3a3a42]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {loading && !stats ? (
            <div className="text-center py-20 text-[var(--muted)]">
              <Loader2 size={26} className="mx-auto text-[var(--orange)] animate-spin" />
              <p className="mt-3 text-[13px]">Loading console...</p>
            </div>
          ) : (
            <>
              {tab === 'grant' && <GrantShares token={token} users={users} companies={companies} onGranted={() => refresh()} />}
              {tab === 'kyc' && <KycReview token={token} users={users} onChanged={() => refresh()} />}
              {tab === 'market' && <AddMarket token={token} onAdded={() => refresh()} />}
              {tab === 'companies' && <Companies companies={companies} />}
              {tab === 'prices' && <PriceControl token={token} />}
              {tab === 'vault' && <CryptoVault token={token} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
